import React, { useState, useEffect, useRef } from 'react';
import { supabase, authService } from '../services/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function ownerApi(action, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || SUPABASE_ANON_KEY;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/owner-api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ action, ...payload }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Request failed');
  return json.data;
}

const ProfileManagement = ({ restaurant, user, onUpdateRestaurant }) => {
  const [profileForm, setProfileForm] = useState({
    restaurantName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    currency: '₹',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    tagline: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (restaurant && user) {
      setProfileForm({
        restaurantName: restaurant.name || '',
        contactEmail: restaurant.contact_email || '',
        contactPhone: restaurant.contact_phone || '',
        address: restaurant.address || '',
        currency: restaurant.default_currency || '₹',
        ownerName: user.full_name || '',
        ownerEmail: user.email || '',
        ownerPhone: user.phone || '',
        tagline: restaurant.tagline || '',
      });
      if (restaurant.logo_url) setLogoPreview(restaurant.logo_url);
    }
  }, [restaurant, user]);

  const validateProfileForm = () => {
    const newErrors = {};

    if (!profileForm.restaurantName.trim()) {
      newErrors.restaurantName = 'Restaurant name is required';
    }

    if (!profileForm.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }

    if (profileForm.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email';
    }

    if (profileForm.ownerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.ownerEmail)) {
      newErrors.ownerEmail = 'Please enter a valid email';
    }

    return newErrors;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const handleProfileUpdate = async () => {
    const profileErrors = validateProfileForm();
    if (Object.keys(profileErrors).length > 0) {
      setErrors(profileErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Upload logo to Supabase Storage if a new file was selected
      let newLogoUrl = restaurant.logo_url || null;
      if (logoFile) {
        setLogoUploading(true);
        const ext = logoFile.name.split('.').pop().toLowerCase();
        const path = `${restaurant.id}/logo_${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('restaurant-logos')
          .upload(path, logoFile, { upsert: true, contentType: logoFile.type });
        setLogoUploading(false);
        if (uploadErr) throw new Error('Logo upload failed: ' + uploadErr.message);
        const { data: { publicUrl } } = supabase.storage
          .from('restaurant-logos')
          .getPublicUrl(path);
        newLogoUrl = publicUrl;
      }

      const taglineVal = profileForm.tagline.trim();
      await ownerApi('updateProfile', {
        restaurantId: restaurant.id,
        restaurantName: profileForm.restaurantName.trim(),
        contactEmail: profileForm.contactEmail.trim(),
        contactPhone: profileForm.contactPhone.trim(),
        address: profileForm.address.trim(),
        currency: profileForm.currency,
        ownerName: profileForm.ownerName.trim(),
        ownerEmail: profileForm.ownerEmail.trim(),
        ownerPhone: profileForm.ownerPhone.trim(),
        tagline: taglineVal,
        logoUrl: newLogoUrl,
      });

      setLogoFile(null);
      if (newLogoUrl) setLogoPreview(newLogoUrl + '?t=' + Date.now());

      // Update the restaurant object in parent component
      if (onUpdateRestaurant) {
        onUpdateRestaurant({
          ...restaurant,
          name: profileForm.restaurantName.trim(),
          contact_email: profileForm.contactEmail.trim(),
          contact_phone: profileForm.contactPhone.trim(),
          address: profileForm.address.trim(),
          default_currency: profileForm.currency,
          tagline: taglineVal || null,
          logo_url: newLogoUrl,
        });
      }

      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ general: error.message || 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    const passwordErrors = validatePasswordForm();
    if (Object.keys(passwordErrors).length > 0) {
      setErrors({ ...errors, ...passwordErrors });
      return;
    }

    setPasswordLoading(true);

    try {
      const { error } = await authService.changePassword(
        user.id,
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      if (error) {
        setErrors({ ...errors, passwordGeneral: error.message });
        return;
      }

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccessMessage('Password updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating password:', error);
      setErrors({ ...errors, passwordGeneral: 'Failed to update password. Please try again.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '30px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Success Message */}
        {successMessage && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#155724',
            textAlign: 'center',
            fontSize: '0.9rem'
          }}>
            {successMessage}
          </div>
        )}

        {/* Restaurant Information */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
          border: '2px solid rgba(255, 68, 68, 0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            margin: '0 0 20px 0',
            color: '#333',
            fontSize: '1.8rem',
            fontWeight: '700',
            paddingBottom: '15px',
            borderBottom: '2px solid #f8f9fa'
          }}>
            Restaurant Information
          </h2>

          {errors.general && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '0.9rem'
            }}>
              {errors.general}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Restaurant Name *
              </label>
              <input
                type="text"
                value={profileForm.restaurantName}
                onChange={(e) => {
                  setProfileForm({ ...profileForm, restaurantName: e.target.value });
                  if (errors.restaurantName) setErrors({ ...errors, restaurantName: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: errors.restaurantName ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
              {errors.restaurantName && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {errors.restaurantName}
                </div>
              )}
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600', 
                color: '#333' 
              }}>
                Restaurant ID
              </label>
              <input
                type="text"
                value={restaurant?.id || ''}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ 
                color: '#6c757d', 
                fontSize: '0.85rem', 
                marginTop: '5px',
                display: 'block'
              }}>
                This is your unique restaurant identifier and cannot be changed
              </small>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Contact Phone
              </label>
              <input
                type="tel"
                value={profileForm.contactPhone}
                onChange={(e) => setProfileForm({ ...profileForm, contactPhone: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Address
              </label>
              <input
                type="text"
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          {/* Currency Field - ADD THIS ENTIRE SECTION */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333'
            }}>
              Restaurant Currency *
            </label>
            <select
              value={profileForm.currency}
              onChange={(e) => setProfileForm({ ...profileForm, currency: e.target.value })}
              style={{
                width: '50%',
                padding: '12px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="₹">₹ Indian Rupee</option>
              <option value="$">$ US Dollar</option>
              <option value="£">£ British Pound</option>
              <option value="€">€ Euro</option>
              <option value="AED">AED UAE Dirham</option>
              <option value="S$">S$ Singapore Dollar</option>
              <option value="A$">A$ Australian Dollar</option>
              <option value="C$">C$ Canadian Dollar</option>
            </select>
            <div style={{
              fontSize: '0.85rem',
              color: '#6c757d',
              marginTop: '5px'
            }}>
              This currency will be used for all menu items and orders
            </div>
          </div>

          {/* Tagline */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Restaurant Tagline
            </label>
            <input
              type="text"
              maxLength={120}
              placeholder="e.g. Authentic flavours, every time"
              value={profileForm.tagline}
              onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '2px solid #e9ecef', borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box' }}
            />
            <small style={{ color: '#6c757d', fontSize: '0.85rem', display: 'block', marginTop: '5px' }}>
              Shown on printed receipts when "Print Tagline" is enabled in Billing Settings.
            </small>
          </div>

          {/* Logo Upload */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Restaurant Logo
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              {logoPreview && (
                <img
                  src={logoPreview}
                  alt="Restaurant logo"
                  style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #e9ecef', background: '#f8f9fa', padding: '4px' }}
                />
              )}
              <div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setLogoFile(file);
                    setLogoPreview(URL.createObjectURL(file));
                  }}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  style={{ background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  {logoPreview ? 'Change Logo' : 'Upload Logo'}
                </button>
                {logoFile && (
                  <div style={{ marginTop: '6px', fontSize: '0.82rem', color: '#16a34a', fontWeight: '600' }}>
                    {logoFile.name} — will be saved when you click Update Profile
                  </div>
                )}
                <div style={{ marginTop: '6px', fontSize: '0.82rem', color: '#6c757d' }}>
                  JPG, PNG or WebP · Max 5 MB
                </div>
                <div style={{ marginTop: '4px', fontSize: '0.82rem', color: '#6c757d' }}>
                  Recommended: <strong>400 × 200 px</strong> (landscape text logo) or <strong>300 × 300 px</strong> (square icon) · PNG with transparent background works best on thermal receipts.
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Owner Information */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
          border: '2px solid rgba(255, 68, 68, 0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            margin: '0 0 20px 0',
            color: '#333',
            fontSize: '1.8rem',
            fontWeight: '700',
            paddingBottom: '15px',
            borderBottom: '2px solid #f8f9fa'
          }}>
            Owner Information
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Full Name *
              </label>
              <input
                type="text"
                value={profileForm.ownerName}
                onChange={(e) => {
                  setProfileForm({ ...profileForm, ownerName: e.target.value });
                  if (errors.ownerName) setErrors({ ...errors, ownerName: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: errors.ownerName ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
              {errors.ownerName && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {errors.ownerName}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Email
              </label>
              <input
                type="email"
                value={profileForm.ownerEmail}
                onChange={(e) => {
                  setProfileForm({ ...profileForm, ownerEmail: e.target.value });
                  if (errors.ownerEmail) setErrors({ ...errors, ownerEmail: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: errors.ownerEmail ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
              {errors.ownerEmail && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {errors.ownerEmail}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Phone
            </label>
            <input
              type="tel"
              value={profileForm.ownerPhone}
              onChange={(e) => setProfileForm({ ...profileForm, ownerPhone: e.target.value })}
              style={{
                width: '48%',
                padding: '12px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>

          <button
            onClick={handleProfileUpdate}
            disabled={loading}
            style={{
              background: loading ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 8px 25px rgba(255, 68, 68, 0.3)'
            }}
          >
            {logoUploading ? 'Uploading logo…' : loading ? 'Updating...' : 'Update Profile'}
          </button>
        </div>

        {/* Password Change */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
          border: '2px solid rgba(255, 68, 68, 0.1)'
        }}>
          <h2 style={{
            margin: '0 0 20px 0',
            color: '#333',
            fontSize: '1.8rem',
            fontWeight: '700',
            paddingBottom: '15px',
            borderBottom: '2px solid #f8f9fa'
          }}>
            Change Password
          </h2>

          {errors.passwordGeneral && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '0.9rem'
            }}>
              {errors.passwordGeneral}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              Current Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value });
                  if (errors.currentPassword) setErrors({ ...errors, currentPassword: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 12px',
                  border: errors.currentPassword ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
              <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                {showCurrentPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {errors.currentPassword}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                New Password *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                    if (errors.newPassword) setErrors({ ...errors, newPassword: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 45px 12px 12px',
                    border: errors.newPassword ? '2px solid #dc2626' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showNewPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {errors.newPassword}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                Confirm New Password *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 45px 12px 12px',
                    border: errors.confirmPassword ? '2px solid #dc2626' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {errors.confirmPassword}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handlePasswordChange}
            disabled={passwordLoading}
            style={{
              background: passwordLoading ? '#ccc' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: passwordLoading ? 'not-allowed' : 'pointer',
              boxShadow: passwordLoading ? 'none' : '0 8px 25px rgba(220, 53, 69, 0.3)'
            }}
          >
            {passwordLoading ? 'Updating...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;